import React, { useState, useEffect } from 'react';
import notificationService from '../services/notificationService';

const NotificationSettings = () => {
  const [isSupported, setIsSupported] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [preferences, setPreferences] = useState({
    bookingUpdates: true,
    reviewNotifications: true,
    reminders: true,
    marketing: false
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    initializeNotifications();
  }, []);

  const initializeNotifications = async () => {
    try {
      setIsSupported(notificationService.isSupported);
      
      if (notificationService.isSupported) {
        await notificationService.init();
        const subscribed = await notificationService.isSubscribed();
        setIsSubscribed(subscribed);

        // Get preferences from backend
        try {
          const prefs = await notificationService.getPreferences();
          setPreferences(prefs.preferences);
          setIsSubscribed(prefs.isSubscribed);
        } catch (error) {
          console.error('Failed to load preferences:', error);
        }
      }
    } catch (error) {
      console.error('Failed to initialize notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async () => {
    try {
      setSaving(true);
      await notificationService.subscribe();
      setIsSubscribed(true);
      alert('Successfully subscribed to notifications! You\'ll receive a welcome notification shortly.');
    } catch (error) {
      console.error('Failed to subscribe:', error);
      alert('Failed to subscribe to notifications. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleUnsubscribe = async () => {
    try {
      setSaving(true);
      await notificationService.unsubscribe();
      setIsSubscribed(false);
      alert('Successfully unsubscribed from notifications.');
    } catch (error) {
      console.error('Failed to unsubscribe:', error);
      alert('Failed to unsubscribe. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handlePreferenceChange = async (key, value) => {
    try {
      const newPreferences = { ...preferences, [key]: value };
      setPreferences(newPreferences);
      
      await notificationService.updatePreferences(newPreferences);
    } catch (error) {
      console.error('Failed to update preferences:', error);
      // Revert the change
      setPreferences(preferences);
      alert('Failed to update preferences. Please try again.');
    }
  };

  const handleTestNotification = async () => {
    try {
      setSaving(true);
      await notificationService.sendTestNotification(
        '🧪 Test Notification',
        'This is a test notification from Sahayak!'
      );
      alert('Test notification sent! Check your notifications.');
    } catch (error) {
      console.error('Failed to send test notification:', error);
      alert('Failed to send test notification. Make sure you\'re subscribed.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="notification-settings">
        <div className="loading">Loading notification settings...</div>
      </div>
    );
  }

  if (!isSupported) {
    return (
      <div className="notification-settings">
        <div className="not-supported">
          <h3>🔔 Push Notifications</h3>
          <p>Push notifications are not supported in your browser. Please use a modern browser like Chrome, Firefox, or Safari.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="notification-settings">
      <h3>🔔 Push Notifications</h3>
      
      {/* Subscription Status */}
      <div className="subscription-status">
        <div className="status-indicator">
          <span className={`status-dot ${isSubscribed ? 'active' : 'inactive'}`}></span>
          <span className="status-text">
            {isSubscribed ? 'Notifications Enabled' : 'Notifications Disabled'}
          </span>
        </div>
        
        <div className="subscription-actions">
          {!isSubscribed ? (
            <button 
              onClick={handleSubscribe} 
              disabled={saving}
              className="btn btn-primary"
            >
              {saving ? 'Enabling...' : 'Enable Notifications'}
            </button>
          ) : (
            <div className="subscribed-actions">
              <button 
                onClick={handleTestNotification} 
                disabled={saving}
                className="btn btn-secondary"
              >
                {saving ? 'Sending...' : 'Test Notification'}
              </button>
              <button 
                onClick={handleUnsubscribe} 
                disabled={saving}
                className="btn btn-outline"
              >
                {saving ? 'Disabling...' : 'Disable Notifications'}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Notification Preferences */}
      {isSubscribed && (
        <div className="notification-preferences">
          <h4>Notification Preferences</h4>
          <p>Choose what types of notifications you want to receive:</p>
          
          <div className="preference-list">
            <div className="preference-item">
              <div className="preference-info">
                <strong>Booking Updates</strong>
                <p>Get notified when bookings are accepted, completed, or cancelled</p>
              </div>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={preferences.bookingUpdates}
                  onChange={(e) => handlePreferenceChange('bookingUpdates', e.target.checked)}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>

            <div className="preference-item">
              <div className="preference-info">
                <strong>Review Notifications</strong>
                <p>Get notified when you receive new reviews (for helpers)</p>
              </div>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={preferences.reviewNotifications}
                  onChange={(e) => handlePreferenceChange('reviewNotifications', e.target.checked)}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>

            <div className="preference-item">
              <div className="preference-info">
                <strong>Booking Reminders</strong>
                <p>Get reminded about upcoming bookings 24 hours and 2 hours before</p>
              </div>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={preferences.reminders}
                  onChange={(e) => handlePreferenceChange('reminders', e.target.checked)}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>

            <div className="preference-item">
              <div className="preference-info">
                <strong>Marketing & Updates</strong>
                <p>Get notified about new features, promotions, and platform updates</p>
              </div>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={preferences.marketing}
                  onChange={(e) => handlePreferenceChange('marketing', e.target.checked)}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>
          </div>
        </div>
      )}

      {/* PWA Install Prompt */}
      <div className="pwa-install">
        <h4>📱 Install App</h4>
        <p>Install Sahayak on your device for the best experience:</p>
        <ul>
          <li>✅ Works offline</li>
          <li>✅ Faster loading</li>
          <li>✅ Native app feel</li>
          <li>✅ Push notifications</li>
        </ul>
        <p className="install-instructions">
          <strong>To install:</strong> Look for the "Add to Home Screen" or "Install" option in your browser menu.
        </p>
      </div>
    </div>
  );
};

export default NotificationSettings; 